import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Button, Checkbox, FormControlLabel, Link, Stack, TextField, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ArrowUpRight, Buildings, Stethoscope, UserCirclePlus } from "@phosphor-icons/react";
import { useSelector } from "react-redux";
import { useQueryClient } from "react-query";
import { ANNoResultsOverlay, ANTableFilters } from "@answersnow/pkg-theme";
import * as selectors from "selectors";
import { useGlobalToast } from "components/GlobalToastProvider";
import { ProviderConfig } from "./ProviderConfig";
import { ProviderForm } from "./ProviderForm";
import { ProviderFormPrivatePractice } from "./ProviderPrivatePracticeForm";
import { ProviderPrivatePracticeConfig } from "./ProviderPrivatePracticeConfig";
import { GET_CLINICS, GET_PROVIDERS } from "../../constants/reactQueryKeys";
import withStyles from "@mui/styles/withStyles";
import styles from "./styles";
import {
  useCreateProvider,
  useGetProviderById,
  useGetProviders,
  useUpdateProvider,
} from "../../hooks/useProvider";
import { useGetClinics, useCreateClinic } from "../../hooks/useClinic";
import AnChipPopper from "../../elements/AnPopper";
import {
  useCreateProvidersClinics,
  useDeleteProvidersClinics,
} from "../../hooks/useProvidersClinics";
import AnDetailsPanelUntyped from "../../elements/Forms/AnDetailsPanel";
import { useHistory } from "react-router-dom";
import useHashParams from "../../hooks/useHashParams";
import { formatToPhoneNumber } from "../../utils/formatFormFields";

// AnDetailsPanel is a plain-JS forwardRef component; TS can't infer a `classes`
// prop for it since withStyles injects that prop at runtime, not in its source.
const AnDetailsPanel = AnDetailsPanelUntyped as React.ForwardRefExoticComponent<
  Record<string, unknown> & React.RefAttributes<unknown>
>;

interface NewClinicFormData {
  clinic_name?: string;
  phone_number?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

interface ClinicRecord {
  id: string;
  clinic_name?: string;
  [key: string]: unknown;
}

interface InputFormItem {
  id: string;
  input: JSX.Element;
  cols?: number;
  headerTitle?: string;
  tab?: number;
  divider?: boolean;
  label?: string;
  paddingTop?: number;
}

interface ProvidersProps {
  classes: Record<string, string>;
  editMode: boolean;
  dataPanel: any;
  resetDataPanel: () => void;
  setDataPanel: (data: any) => void;
  configPanel: any;
  setOpenWarning: (open: boolean) => void;
  unsavedChanges: boolean;
  setEditMode: (editMode: boolean) => void;
  updateActivePanel: (panel: string | null) => void;
  updateDataPanel: (name: string, value: unknown) => void;
  setUnsavedChanges: (unsaved: boolean) => void;
  activePanel: string | null;
  isDirty: boolean;
  updateIsDirty: (isDirty: boolean) => void;
  setIsCancelClicked: (cancelled: boolean) => void;
  panelRef: React.Ref<unknown>;
  clearData: () => void;
}

const Providers = (props: ProvidersProps) => {
  const {
    classes,
    editMode,
    dataPanel,
    resetDataPanel,
    setDataPanel,
    configPanel,
    setOpenWarning,
    unsavedChanges,
    setEditMode,
    updateActivePanel,
    updateDataPanel,
    setUnsavedChanges,
    activePanel,
    isDirty,
    updateIsDirty,
    setIsCancelClicked,
    panelRef,
    clearData,
  } = props;
  const theme = useTheme();
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const orderByMap: Record<string, { sort: string; direction: string }> = {
    first_name: { sort: "first_name", direction: "asc" },
    last_name: { sort: "last_name", direction: "asc" },
  };
  const params = useHashParams();
  const [orderBy, setOrderBy] = useState(orderByMap.first_name.sort);
  const [byFilterAcceptedInsurance, setByFilterAcceptedInsurance] = useState<string[]>([]);
  const [byFilterClinics, setByFilterClinics] = useState<string[]>([]);
  const [byFilterProvider, setByFilterProvider] = useState<string[]>([]);
  const [byFilterClients, setByFilterClients] = useState("");
  const [, setUploadedDateFilter] = useState([null, null]);
  const [selectedClinics, setSelectedClinics] = useState<string[]>([]);
  const [newClinicData, setNewClinicData] = useState<NewClinicFormData>({});
  const userId = useSelector(selectors.getUserId);
  const { showToast } = useGlobalToast();
  const queryClient = useQueryClient();
  const history = useHistory();
  const [inputsForm, setInputsForms] = React.useState<InputFormItem[]>([]);
  const [isPanelOpen, setIsPanelOpen] = React.useState(false);
  const [copiedId, setCopiedId] = React.useState<string | null>(null);

  const handleCopyId = React.useCallback((id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 3000);
  }, []);

  const { isFetching, data: response } = useGetProviders(
    {
      sort: orderByMap[orderBy]?.sort,
      direction: orderByMap[orderBy]?.direction,
      page: paginationModel.page,
      size: paginationModel.pageSize,
      accept_insurance: byFilterAcceptedInsurance.join(","),
      clinics: byFilterClinics.join(","),
      provider_filter: byFilterProvider.join(","),
    },
    {
      onSuccess: (data: any) => {
        if ((activePanel === "deleteClinic" || activePanel === "createClinic") && dataPanel.id) {
          const row = data?.data?.providers.filter((value: any) => value.id === dataPanel.id);
          if (row && row.length > 0) {
            setUnsavedChanges(false);
            setDataPanel(row[0]);
            setInputsForms(inputForms(row[0], []));
          } else {
            if (params.size > 0 && String(params.get("id_provider")) === String(dataPanel?.id)) {
              getProviderByIdMutate({ id: params.get("id_provider") });
            }
          }
        }
      },
    }
  );

  const { isFetching: isFetchingClinics, data: clinics } = useGetClinics(
    {
      sort: "clinic_name",
      direction: "asc",
      page: 0,
      size: 1000,
      clinic_name: byFilterClients,
    },
    {
      onSuccess: () => {},
    }
  );

  const fetchedData = response?.data.providers;
  const rowCount = Number(response?.data.count) || 0;
  const acceptsInsuranceFilter = response?.data.accepted_insurance;
  const clinicsFilter = response?.data.clinics;
  const providersFilter = response?.data.providers_filter;

  const generateRows = () => {
    if (!fetchedData) return [];
    return fetchedData.map((data) => ({
      id: data.id,
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      fax_number: formatToPhoneNumber(data.fax_number ?? ""),
      npi_number: data.npi_number,
      medicaid_id: data.medicaid_id || "",
      phone: formatToPhoneNumber(data.phone),
      specialty: data.specialty,
      accepts_insurance: data.accepts_insurance,
      accepts_insurance_text: data.accepts_insurance === true ? "Yes" : "No",
      physical_address: data.physical_address?.split(" | ")[0] || "",
      physical_address_2: data.physical_address?.split(" | ")[1] || "",
      city: data.city || "",
      state: data.state || "",
      zip_code: data.zip_code || "",
      website: data.website || "",
      physician_clinic: data.physician_clinic,
    }));
  };

  useEffect(() => {
    if (params.size > 0) {
      getProviderByIdMutate({ id: params.get("id_provider") });
    }
  }, [params]);

  const rows = generateRows();

  const getColumnDefinitions = () => {
    const defaultColAttrs = {
      sortable: false,
      flex: 1,
    };
    const columns = [
      {
        field: "first_name",
        headerName: "First Name",
        ...defaultColAttrs,
      },
      {
        field: "last_name",
        headerName: "Last Name",
        ...defaultColAttrs,
      },
      {
        field: "id",
        headerName: "ID Number",
        ...defaultColAttrs,
        renderCell: (params: any) => {
          const id = params.row.id;
          const isCopied = copiedId === id;
          return (
            <Tooltip title={isCopied ? "Copied!" : "Copy to clipboard"}>
              <Stack onClick={() => handleCopyId(id)} sx={{ cursor: "pointer" }}>
                {id}
              </Stack>
            </Tooltip>
          );
        },
      },
      {
        field: "physician_clinic",
        headerName: "Clinics",
        ...defaultColAttrs,
        renderCell: (params: any) => {
          const physicianClinic = params.row.physician_clinic;
          const providerCount = physicianClinic.length;
          let labelText = `${providerCount} Clinics`;
          if (providerCount === 1) {
            labelText = `${physicianClinic[0].clinic.clinic_name}`;
          }
          return (
            <AnChipPopper
              list={physicianClinic}
              chipIcon={
                <Buildings
                  size={16}
                  color={theme.palette.primary.main}
                  style={{ flex: "none" }}
                  weight="duotone"
                />
              }
              linkIcon={
                <ArrowUpRight weight="regular" color={theme.palette.primary.main} size={16} />
              }
              label={labelText}
              getLink={(item: any) => {
                return `libraries#clinics?id_clinic=${item.clinic.id}`;
              }}
              getLabel={(item: any) => {
                return `${item.clinic.clinic_name}`;
              }}
            />
          );
        },
      },
      { field: "email", headerName: "Email Address", ...defaultColAttrs },
      {
        field: "fax_number",
        headerName: "Fax Number",
        ...defaultColAttrs,
      },
      {
        field: "phone",
        headerName: "Phone  Number",
        ...defaultColAttrs,
      },
      {
        field: "npi_number",
        headerName: "NPI  Number",
        ...defaultColAttrs,
      },
      {
        field: "medicaid_id",
        headerName: "Medicaid Number",
        ...defaultColAttrs,
      },
      {
        field: "specialty",
        headerName: "Specialty",
        ...defaultColAttrs,
      },
      {
        field: "accepts_insurance_text",
        headerName: "Accepts Insurance",
        ...defaultColAttrs,
      },
      {
        field: "action",
        headerName: "Action",
        ...defaultColAttrs,
        renderCell: (params: any) => {
          return (
            <Link
              color="primary"
              variant="body2"
              underline="hover"
              onClick={() => handleEditItem(params.row)}
              sx={{ "&:hover": { cursor: "pointer" } }}
            >
              Edit
            </Link>
          );
        },
      },
    ];
    return columns;
  };

  const orderingMenuItems = [
    { label: "First Name", value: "first_name" },
    { label: "Last Name", value: "last_name" },
  ];

  const filtersMenuItems = [
    {
      label: "Accepts Insurance",
      value: "state",
      countLabel: "provider",
      icon: <Stethoscope weight="duotone" />,
      countObject: acceptsInsuranceFilter ?? [],
      state: byFilterAcceptedInsurance,
      setState: setByFilterAcceptedInsurance,
      showChipConditionCustomComponent: false,
    },
    {
      label: "Clinics",
      value: "clinics",
      countLabel: "provider",
      icon: <Buildings weight="duotone" />,
      countObject: clinicsFilter ?? [],
      state: byFilterClinics,
      setState: setByFilterClinics,
      searchInput: true,
      showChipConditionCustomComponent: false,
    },
    {
      label: "Providers",
      value: "provider_filter",
      countLabel: "clinic",
      icon: <Stethoscope weight="duotone" />,
      countObject: providersFilter ?? [],
      state: byFilterProvider,
      setState: setByFilterProvider,
      searchInput: true,
      showChipConditionCustomComponent: false,
    },
  ];

  const handleClearFilters = () => {
    setByFilterClinics([]);
    setByFilterAcceptedInsurance([]);
    setByFilterProvider([]);
    setUploadedDateFilter([null, null]);
  };

  const customNoRowsOverlay = {
    NoResults: {
      component: ANNoResultsOverlay,
      height: "236px",
      props: { handleClearFilters },
    },
  };

  const overlayName = "NoResults";

  const validateRequired = (data: any, type: any) => {
    const errors: Record<string, boolean> = {};
    updateIsDirty(false);
    const config = data["private_practice"] ? ProviderPrivatePracticeConfig : ProviderConfig;
    const requiredIds = config.filter((item) => item.required).map((item) => item.id);
    requiredIds.forEach((id) => {
      if (type === "zipCode" && id.includes("zip")) {
        if (data[id]?.length < 5) {
          updateIsDirty(true);
          errors[id + "_format_error"] = true;
        } else if (data[id] === undefined || data[id] === null || data[id].length === 0) {
          updateIsDirty(true);
          errors[id] = true;
        } else {
          errors[id + "_format_error"] = false;
          errors[id] = false;
        }
      } else if (data[id] === undefined || data[id] === null || data[id].length === 0) {
        updateIsDirty(true);
        errors[id] = true;
      } else {
        errors[id] = false;
      }
    });
    if (!data?.physician_clinic?.length && !data.private_practice) {
      updateIsDirty(true);
      errors["physician_clinic"] = true;
      errors["private_practice"] = true;
    }
    return errors;
  };

  const onChange = (name: any, onChangeType: any, data: any) => (e: any) => {
    const { checked, type, value } = e.target;
    let needValidateRequired = true;
    if (!onChangeType) {
      const val = type === "checkbox" ? checked : value;
      data[name] = val;
      updateDataPanel(name, val);
    } else if (onChangeType === "radioButtonChange") {
      let parsedValue;
      if (typeof value === "boolean") {
        parsedValue = value;
      } else {
        parsedValue = value === "true";
      }
      data[name] = parsedValue;
      updateDataPanel(name, parsedValue);
    } else if (onChangeType === "phoneNumberchange") {
      const phone = value.replace(/[^\d]/g, "");
      data[name] = phone;
      updateDataPanel(name, phone);
    } else if (onChangeType === "numberChange") {
      data[name] = value.replace(/[^0-9]/g, "");
      updateDataPanel(name, value.replace(/[^0-9]/g, ""));
    } else if (onChangeType === "zipCode") {
      data[name] = value;
      updateDataPanel(name, value);
    } else if (onChangeType === "physician_clinic") {
      if (!data["physician_clinic"]) {
        data["physician_clinic"] = [];
      }
      const clinic = clinics?.data?.clinics.find((clinic) => clinic.id === value);
      if (clinic) {
        data["physician_clinic"].push({
          id: clinic.id,
          clinic_id: clinic.id,
          clinic: { ...clinic },
        });
      }
      needValidateRequired = false;
    }
    setUnsavedChanges(true);
    setInputsForms(
      inputForms(data, needValidateRequired ? validateRequired(data, onChangeType) : data)
    );
  };

  const onCheckboxSelected = (clinicId: string) => {
    setSelectedClinics((prevSelected) => {
      return prevSelected.includes(clinicId)
        ? prevSelected.filter((id) => id !== clinicId)
        : [...prevSelected, clinicId];
    });
  };

  const onNewClinicChange =
    (name: keyof NewClinicFormData, onChangeType?: string) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const val = onChangeType === "phoneNumberchange" ? value.replace(/[^\d]/g, "") : value;
      setNewClinicData((prev) => ({ ...prev, [name]: val }));
    };

  const getIsNewClinicValid = () =>
    Boolean(
      newClinicData.clinic_name &&
        newClinicData.phone_number &&
        newClinicData.address &&
        newClinicData.city &&
        newClinicData.state &&
        newClinicData.zip_code?.length === 5
    );

  const onSubmitClinics = async () => {
    if (dataPanel?.id) {
      updateActivePanel("createClinic");
      const payloads = selectedClinics.map((value) => ({
        clinic_id: value,
        physician_id: dataPanel.id,
        created_by: userId,
      }));
      await createProvidersClinicsMutateAsync(payloads);
      updateIsDirty(false);
    } else {
      selectedClinics.forEach((value) => {
        const clinicExists = dataPanel.physician_clinic.some(
          (clinic: any) => clinic.clinic_id === value
        );
        if (!clinicExists) {
          onChange("physician_clinic", "physician_clinic", dataPanel)({ target: { value } });
        }
      });
    }
  };

  const removeClinic = async (clinicId: any, data: any) => {
    if (data?.id) {
      updateActivePanel("deleteClinic");
      deleteProvidersClinicsMutate(clinicId);
      updateIsDirty(false);
    } else {
      if (data["physician_clinic"]) {
        const clinicIndex = data["physician_clinic"].findIndex(
          (item: any) => item.id === clinicId
        );
        if (clinicIndex !== -1) {
          data["physician_clinic"].splice(clinicIndex, 1);
          setInputsForms(inputForms(data, validateRequired(data, "physician_clinic")));
        }
      }
    }
  };

  const inputForms = (data: any, errors: any): InputFormItem[] => {
    const inputs: InputFormItem[] = [
      {
        id: "first_name",
        input: (
          <TextField
            id="first_name"
            label="First name"
            onChange={onChange("first_name", null, data)}
            value={data.first_name ?? ""}
            fullWidth
          />
        ),
        cols: 12,
        headerTitle: "Provider Details",
      },
      {
        id: "last_name",
        input: (
          <TextField
            id="last_name"
            label="Last name"
            onChange={onChange("last_name", null, data)}
            value={data.last_name ?? ""}
            fullWidth
          />
        ),
        cols: 12,
      },
      {
        id: "medicaid_id",
        input: (
          <TextField
            id="medicaid_id"
            label="Medicaid ID (optional)"
            onChange={onChange("medicaid_id", null, data)}
            value={data.medicaid_id ?? ""}
            fullWidth
          />
        ),
        cols: 12,
      },
      {
        id: "npi_number",
        input: (
          <TextField
            id="npi_number"
            label="NPI number (optional)"
            onChange={onChange("npi_number", null, data)}
            value={data.npi_number ?? ""}
            fullWidth
          />
        ),
        cols: 12,
      },
      {
        id: "specialty",
        input: (
          <TextField
            id="specialty"
            label="Specialty (optional)"
            onChange={onChange("specialty", null, data)}
            value={data.specialty ?? ""}
            fullWidth
          />
        ),
        cols: 12,
      },
      {
        id: "accepts_insurance",
        tab: 0,
        input: (
          <FormControlLabel
            control={
              <Checkbox
                checked={data.accepts_insurance}
                onChange={onChange("accepts_insurance", null, data)}
              />
            }
            label="Accepts insurance"
          />
        ),
        cols: 12,
        divider: true,
      },
      {
        id: "email",
        input: (
          <TextField
            id="email"
            label="Email address"
            onChange={onChange("email", null, data)}
            value={data.email ?? ""}
            fullWidth
          />
        ),
        cols: 12,
      },
      {
        id: "phone",
        tab: 0,
        input: (
          <TextField
            label="Providers's phone number"
            id="phone"
            onChange={onChange("phone", "phoneNumberchange", data)}
            value={formatToPhoneNumber(data.phone ?? "")}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{ maxLength: 14 }}
          />
        ),
      },
      {
        id: "fax_number",
        tab: 0,
        input: (
          <TextField
            label="Providers's fax number (optional)"
            id="fax_number"
            onChange={onChange("fax_number", "phoneNumberchange", data)}
            value={formatToPhoneNumber(data.fax_number ?? "")}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{ maxLength: 14 }}
          />
        ),
        divider: true,
      },
    ];
    const hasNoClinic = !data?.physician_clinic?.length;
    if (hasNoClinic) {
      inputs.push({
        id: "private_practice",
        tab: 0,
        headerTitle: "Clinics",
        headerDescription: "Connect the provider to clinics from the clinic library",
        input: (
          <FormControlLabel
            control={
              <Checkbox
                checked={data.private_practice}
                onChange={onChange("private_practice", null, data)}
              />
            }
            label="Set as Private Practice"
          />
        ),
        cols: 12,
      } as InputFormItem);
    }
    if (data["private_practice"]) {
      inputs.push(...ProviderFormPrivatePractice({ data, errors, onChange }));
    } else {
      inputs.push(
        ...ProviderForm({
          classes,
          data,
          onDeleteItem: (clinicId: any) => removeClinic(clinicId, data),
          theme,
          isDeletingItem,
          clinics: clinics?.data?.clinics,
          onSearch: setByFilterClients,
          onClinicSelected: onCheckboxSelected,
          onSubmitClinics,
          isCreatingItem,
          newClinicData,
          onNewClinicChange,
          onCreateNewClinic,
          isNewClinicValid: getIsNewClinicValid(),
          isCreatingClinic,
          hasSelectedClinics: selectedClinics.length > 0,
        })
      );
    }

    return inputs;
  };

  const { mutate: createMutate, isLoading: isCreating } = useCreateProvider({
    onSuccess: () => {
      clearData();
      setIsPanelOpen(false);
      setSelectedClinics([]);
      showToast({ message: "Provider Created Successfully!" });
      queryClient.invalidateQueries([GET_CLINICS]);
      queryClient.invalidateQueries([GET_PROVIDERS]);
    },
    onError: () => {
      showToast({
        message: "Error Creating Provider.",
        errorState: true,
      });
    },
  });

  const { mutate: updateMutate, isLoading: isUpdating } = useUpdateProvider({
    onSuccess: () => {
      clearData();
      setIsPanelOpen(false);
      setSelectedClinics([]);
      showToast({ message: "Provider Updated Successfully!" });
      queryClient.invalidateQueries([GET_PROVIDERS]);
    },
    onError: () => {
      showToast({
        message: "Error Updating Provider.",
        errorState: true,
      });
    },
  });

  const { mutate: deleteProvidersClinicsMutate, isLoading: isDeletingItem } =
    useDeleteProvidersClinics({
      onSuccess: () => {
        setSelectedClinics([]);
        queryClient.invalidateQueries([GET_PROVIDERS]);
        if (params.size > 0 && String(params.get("id_provider")) === String(dataPanel?.id)) {
          getProviderByIdMutate({ id: params.get("id_provider") });
        }
        showToast({ message: "Clinic Removed Successfully!" });
      },
      onError: () => {
        showToast({
          message: "Error Deleting Clinic of Provider..",
          errorState: true,
        });
      },
    });

  // No onError toast here: both callers (onSubmitClinics as AnItemControlPanel's
  // onSubmitDialog, and onCreateNewClinic as its onAddNewSubmit) await this mutation,
  // so AnItemControlPanel's own catch already shows an error toast and keeps the
  // dialog open when it rejects — a toast here too would double up.
  const { mutateAsync: createProvidersClinicsMutateAsync, isLoading: isCreatingItem } =
    useCreateProvidersClinics({
      onSuccess: () => {
        setSelectedClinics([]);
        queryClient.invalidateQueries([GET_PROVIDERS]);
        if (params.size > 0 && String(params.get("id_provider")) === String(dataPanel?.id)) {
          getProviderByIdMutate({ id: params.get("id_provider") });
        }
        showToast({ message: "Clinics Connected Successfully!" });
      },
    }) as unknown as {
      mutateAsync: (
        payload: Array<{ clinic_id: string; physician_id: unknown; created_by: unknown }>
      ) => Promise<unknown>;
      isLoading: boolean;
    };

  // No onError toast here: onCreateNewClinic is only ever invoked as
  // AnItemControlPanel's onAddNewSubmit, which already shows an error toast
  // and keeps the dialog open when this rejects.
  const { mutateAsync: createClinicAsync, isLoading: isCreatingClinic } = useCreateClinic(
    {}
  ) as unknown as {
    mutateAsync: (
      payload: NewClinicFormData & { created_by: unknown }
    ) => Promise<{ data: ClinicRecord }>;
    isLoading: boolean;
  };

  const onCreateNewClinic = async () => {
    const response = await createClinicAsync({ created_by: userId, ...newClinicData });
    const newClinic = response.data;
    if (dataPanel?.id) {
      updateActivePanel("createClinic");
      await createProvidersClinicsMutateAsync([
        { clinic_id: newClinic.id, physician_id: dataPanel.id, created_by: userId },
      ]);
      updateIsDirty(false);
    } else {
      if (!dataPanel["physician_clinic"]) {
        dataPanel["physician_clinic"] = [];
      }
      dataPanel["physician_clinic"].push({
        id: newClinic.id,
        clinic_id: newClinic.id,
        clinic: newClinic,
      });
      setUnsavedChanges(true);
      setInputsForms(inputForms(dataPanel, validateRequired(dataPanel, "physician_clinic")));
    }
    setNewClinicData({});
    queryClient.invalidateQueries([GET_CLINICS]);
  };

  const onSave = async () => {
    if (!isDirty) {
      if (dataPanel?.private_practice) {
        dataPanel.physical_address = `${dataPanel?.physical_address}${
          dataPanel?.physical_address_2 ? " | " + dataPanel.physical_address_2 : ""
        }`.trim();
      } else {
        delete dataPanel.physical_address;
        delete dataPanel.city;
        delete dataPanel.state;
        delete dataPanel.zip_code;
        delete dataPanel.website;
      }
      delete dataPanel.physical_address_2;
      delete dataPanel.accepts_insurance_text;
      dataPanel.phone = dataPanel?.phone.replace(/[^\d]/g, "");
      dataPanel.fax_number = dataPanel?.fax_number?.replace(/[^\d]/g, "");
      if (dataPanel?.id) {
        updateMutate({ id: dataPanel?.id, ...dataPanel });
      } else {
        createMutate({ ...dataPanel });
      }
    }
  };

  useEffect(() => {
    if (!isFetchingClinics) {
      setInputsForms(inputForms(dataPanel, validateRequired(dataPanel, "")));
    }
  }, [clinics, dataPanel, newClinicData]);

  useEffect(() => {
    // Only stage the selection directly onto dataPanel.physician_clinic when creating a
    // brand-new (unsaved) provider — there's no id yet to link against via the API, so the
    // clinic has to travel along with the rest of the form until it's submitted. When editing
    // an existing provider, selections stay local to `selectedClinics` until "Connect Clinic"
    // is actually clicked (see onSubmitClinics); mutating dataPanel here would make the clinic
    // vanish from the pickable list before the connection is real.
    if (selectedClinics && selectedClinics.length > 0 && !dataPanel?.id) {
      onChange(
        "clinic_id",
        "physician_clinic",
        dataPanel
      )({ target: { value: selectedClinics[selectedClinics.length - 1] } });
    } else {
      setInputsForms(inputForms(dataPanel, validateRequired(dataPanel, "")));
    }
  }, [selectedClinics]);

  const handleEditItem = (row: any) => {
    setIsPanelOpen(true);
    setEditMode(true);
    updateIsDirty(true);
    setUnsavedChanges(false);
    updateActivePanel("createProvider");
    setDataPanel(row);
    setInputsForms(inputForms(row, []));
  };

  const { mutate: getProviderByIdMutate } = useGetProviderById({
    onSuccess: (response: any) => {
      handleEditItem(response.data);
    },
  }) as unknown as { mutate: (payload: { id: string | null }) => void };

  return (
    <Stack
      direction="row"
      sx={{
        height: "calc(100vh - 163px)",
        [theme.breakpoints.down("md")]: {
          height: "calc(100vh - 171px)",
        },
        width: "100%",
      }}
    >
      <Stack
        flex={isPanelOpen ? 2 : 1}
        sx={{
          transition: "all 0.3s ease",
          overflow: "auto",
        }}
      >
        <div className={classes.header} style={{ padding: "16px" }}>
          <div className={classes.headerButtonContainer}>
            <ANTableFilters
              orderingMenuItems={orderingMenuItems}
              orderBy={orderBy}
              setOrderBy={setOrderBy}
              filtersMenuItems={filtersMenuItems}
              filtersButtonProps={{ disabled: fetchedData?.length === 0 }}
            />
          </div>
          <div className={classes.headerButtonContainer}>
            <Button
              color="secondary"
              startIcon={<UserCirclePlus />}
              size="small"
              onClick={() => {
                setSelectedClinics([]);
                setIsPanelOpen(true);
                setEditMode(true);
                updateIsDirty(true);
                setUnsavedChanges(false);
                updateActivePanel("createProvider");
                setInputsForms(inputForms({}, []));
              }}
              sx={{ minWidth: "fit-content" }}
            >
              Add Provider
            </Button>
          </div>
        </div>
        <div>
          <DataGrid
            rows={rows}
            loading={isFetching && !fetchedData}
            pageSizeOptions={[5, 10, 20]}
            columns={getColumnDefinitions()}
            disableRowSelectionOnClick
            autoHeight
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            slots={{
              noRowsOverlay: customNoRowsOverlay[overlayName].component,
            }}
            slotProps={{
              noRowsOverlay: { ...customNoRowsOverlay[overlayName].props } as Record<
                string,
                unknown
              >,
            }}
            sx={{ "--DataGrid-overlayHeight": customNoRowsOverlay[overlayName].height }}
            hideFooter={!rowCount}
            paginationMode="server"
            rowCount={rowCount}
          />
        </div>
      </Stack>

      <Stack
        flex={isPanelOpen ? 2 : 0}
        sx={{
          borderLeft: `1px solid ${theme.palette.divider}`,
          transition: "all 0.3s ease",
          alignItems: "flex-end",
          overflow: "auto",
          display: "flow",
        }}
        maxWidth="400px"
      >
        <AnDetailsPanel
          ref={panelRef}
          classes={classes}
          open={isPanelOpen}
          onClosePanel={() => setIsPanelOpen(false)}
          editMode={editMode}
          titlePanel={dataPanel?.id ? "Edit Provider" : "Add Provider"}
          data={dataPanel}
          resetData={resetDataPanel}
          config={configPanel}
          inputsForm={inputsForm}
          isLoading={isCreating || isUpdating || isDeletingItem || isCreatingItem}
          disableSaveButton={isDirty}
          onSave={onSave}
          onCancel={() => {
            if (unsavedChanges) {
              setIsCancelClicked(true);
              setOpenWarning(true);
            } else {
              clearData();
              setIsPanelOpen(false);
              setSelectedClinics([]);
              updateActivePanel(null);
              history.push("#providers");
            }
          }}
          saveTitle={"Save Changes"}
          icon={
            <UserCirclePlus
              fontSize={"20px"}
              weight="duotone"
              color={theme.palette.primary.main}
            ></UserCirclePlus>
          }
          topEditEnable={false}
        />
      </Stack>
    </Stack>
  );
};
export default withStyles(styles as any)(Providers);
